import { useEffect } from 'react';
import { Extension } from '@tiptap/core';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { vAlignToJustify, type RichText, type BlockVAlign } from '@jobos/shared';

import { richTextToTiptapDoc, tiptapDocToRichText, type TiptapDoc } from '@/lib/inspectionRichText';

// Block-level font size, stored as a node attribute on paragraph/heading and
// rendered as inline `font-size:<n>pt` — mirrors how the PDF generator renders it.
const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['paragraph'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (el: HTMLElement) => {
              const s = el.style.fontSize;
              return s ? parseInt(s, 10) : null;
            },
            renderHTML: (attrs: { fontSize?: number | null }) =>
              attrs.fontSize ? { style: `font-size:${attrs.fontSize}pt` } : {},
          },
        },
      },
    ];
  },
});

// The constrained editor: only the marks/nodes we allow are even registered, so
// (unlike Word) there is nothing else the user can produce.
export const inspectionEditorExtensions = [
  StarterKit.configure({
    // Disable everything outside the constrained set (incl. headings — body
    // text only — so `## ` can't create a heading either).
    heading: false,
    blockquote: false,
    bulletList: false,
    orderedList: false,
    listItem: false,
    code: false,
    codeBlock: false,
    horizontalRule: false,
    strike: false,
    link: false,
  }),
  TextAlign.configure({ types: ['paragraph'] }),
  FontSize,
];

export interface InlineRichTextProps {
  value: RichText;
  onChange: (value: RichText) => void;
  onFocusEditor?: (editor: Editor) => void;
  onBlurEditor?: () => void;
  vAlign?: BlockVAlign;
}

export function InlineRichText({ value, onChange, onFocusEditor, onBlurEditor, vAlign }: InlineRichTextProps) {
  const editor = useEditor({
    extensions: inspectionEditorExtensions,
    content: richTextToTiptapDoc(value),
    onUpdate: ({ editor: ed }) => onChange(tiptapDocToRichText(ed.getJSON() as unknown as TiptapDoc)),
    onFocus: ({ editor: ed }) => onFocusEditor?.(ed),
    onBlur: () => onBlurEditor?.(),
  });

  // Tear down the editor instance on unmount (e.g. page switch / remount-by-key).
  useEffect(() => () => editor?.destroy(), [editor]);

  // The wrapper fills the whole cell and vertically positions the content; the
  // editable area itself is content-height. Clicking the empty area (the gap
  // left by vertical alignment) focuses the editor so the entire box is a
  // click target, not just the text.
  return (
    <div
      className="ins-rt"
      style={{ justifyContent: vAlignToJustify(vAlign) }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && editor) {
          e.preventDefault();
          editor.chain().focus().run();
        }
      }}
    >
      <EditorContent editor={editor} />
    </div>
  );
}
